(() => {
class SandlotReviews extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.labels = JSON.parse(this.querySelector('[data-review-labels]').textContent);
    this.list = this.querySelector('[data-review-list]');
    this.status = this.querySelector('[data-review-status]');
    this.more = this.querySelector('[data-review-more]');
    this.sort = this.querySelector('[data-review-sort]');
    this.page = 0;
    this.shown = new Set();
    this.highlightsLoaded = false;
    this.more.addEventListener('click', () => this.load());
    this.sort.addEventListener('change', () => this.load(true));
    this.querySelector('[data-highlight-cards]').addEventListener('click', async event => {
      const button = event.target.closest('[data-review-id]');
      if (!button) return;
      const id = `${this.id}-review-${button.dataset.reviewId}`;
      if (!this.querySelector(`[id="${id}"]`)) {
        this.sort.value = 'date-desc';
        await this.load(true);
      }
      const review = this.querySelector(`[id="${id}"]`);
      if (review) {
        review.focus({ preventScroll: true });
        review.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
    });
    // Read published reviews only. No private token, submissions, votes, or account changes.
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect();
        this.load();
      }
    }, { rootMargin: '600px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.initialized = false;
  }

  text(value) {
    // Yotpo may HTML-encode text. Decode as inert text, never insert review HTML.
    const doc = new DOMParser().parseFromString(String(value ?? ''), 'text/html');
    return doc.body.textContent.trim();
  }

  node(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  stars(score) {
    const rating = Math.max(0, Math.min(5, Number(score) || 0));
    const stars = this.node('span', 'sg-reviews__stars', '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating)));
    stars.setAttribute('role', 'img');
    stars.setAttribute('aria-label', this.labels.stars.replace('{score}', rating));
    return stars;
  }

  author(review) {
    const author = this.node('p', 'sg-reviews__author', this.text(review.user?.display_name) || this.labels.anonymous);
    if (review.verified_buyer) author.append(this.node('span', 'sg-reviews__badge', this.labels.verified));
    if (review.is_incentivized) author.append(this.node('span', 'sg-reviews__badge', this.labels.incentivized));
    return author;
  }

  productName(review, data) {
    const group = data.grouping_data?.[review.id];
    const syndication = data.syndication_data?.[review.id];
    return this.text(group?.product_name || syndication?.product_name || data.products?.find(product => product.id === review.product_id)?.name);
  }

  review(review, data) {
    const row = this.node('article', 'sg-reviews__review');
    row.id = `${this.id}-review-${review.id}`;
    row.tabIndex = -1;
    const meta = this.node('div');
    meta.append(this.author(review));
    const date = new Date(review.created_at);
    if (!Number.isNaN(date.getTime())) {
      const time = this.node('time', '', date.toLocaleDateString(document.documentElement.lang || 'en', { month: 'short', day: 'numeric', year: 'numeric' }));
      time.dateTime = date.toISOString();
      meta.append(time);
    }
    const name = this.productName(review, data);
    if (name) meta.append(this.node('p', 'sg-reviews__product', name));
    const content = this.node('div');
    content.append(this.stars(review.score));
    if (review.title) content.append(this.node('h4', '', this.text(review.title)));
    content.append(this.node('p', 'sg-reviews__body', this.text(review.content)));
    if (review.images_data?.length) {
      const photos = this.node('div', 'sg-reviews__photos');
      for (const photo of review.images_data) {
        // Only display public, secure photo URLs from the review payload.
        if (!/^https:\/\//.test(photo.thumb_url || '')) continue;
        const img = this.node('img');
        img.src = photo.thumb_url;
        img.alt = name || this.text(review.title);
        img.loading = 'lazy';
        img.width = img.height = 80;
        photos.append(img);
      }
      content.append(photos);
    }
    if (review.comment?.content) {
      const response = this.node('div', 'sg-reviews__response');
      response.append(this.node('strong', '', this.labels.response), this.node('p', '', this.text(review.comment.content)));
      content.append(response);
    }
    row.append(meta, content);
    return row;
  }

  highlights(reviews, data) {
    const cards = this.querySelector('[data-highlight-cards]');
    const selected = reviews.filter(review => Number(review.score) >= 4 && this.text(review.content)).slice(0, 3);
    for (const review of selected) {
      const card = this.node('div', 'sg-reviews__highlight');
      const fullQuote = this.text(review.content);
      const quote = fullQuote.length > 180 ? fullQuote.slice(0, 180).replace(/\s+\S*$/, '') + '…' : fullQuote;
      card.append(this.stars(review.score), this.node('blockquote', '', `“${quote}”`), this.author(review));
      const name = this.productName(review, data);
      if (name) card.append(this.node('p', 'sg-reviews__product', name));
      const button = this.node('button', 'sg-reviews__read', this.labels.read);
      button.type = 'button';
      button.dataset.reviewId = review.id;
      card.append(button);
      cards.append(card);
    }
    this.querySelector('[data-review-highlights]').hidden = !selected.length;
    this.highlightsLoaded = true;
  }

  async load(reset = false) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.status.textContent = this.labels.loading;
    this.more.disabled = true;
    this.sort.disabled = true;
    this.list.setAttribute('aria-busy', 'true');
    const page = reset ? 1 : this.page + 1;
    try {
      if (!/^[a-zA-Z0-9_-]+$/.test(this.dataset.appKey || '') || !/^\d+$/.test(this.dataset.productId || '')) throw new Error('Missing public review configuration');
      const [sort, direction] = this.sort.value.split('-');
      const url = new URL(`https://api-cdn.yotpo.com/v1/widget/${this.dataset.appKey}/products/${this.dataset.productId}/reviews.json`);
      url.search = new URLSearchParams({ per_page: '10', page: String(page), sort, direction });
      const timeout = setTimeout(() => controller.abort(), 15000);
      let payload;
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
        if (!response.ok) throw new Error('Review service unavailable');
        payload = await response.json();
      } finally { clearTimeout(timeout); }
      if (Number(payload.status?.code) !== 200 || !Array.isArray(payload.response?.reviews)) throw new Error('Invalid review response');
      const data = payload.response;
      if (reset) { this.list.replaceChildren(); this.shown.clear(); }
      const reviews = data.reviews.filter(review => !review.deleted);
      if (!this.highlightsLoaded) this.highlights(reviews, data);
      for (const review of reviews) {
        if (this.shown.has(review.id)) continue;
        this.list.append(this.review(review, data));
        this.shown.add(review.id);
      }
      this.page = page;
      const total = Number(data.pagination?.total) || 0;
      const summary = this.querySelector('[data-review-summary]');
      summary.replaceChildren();
      if (total) {
        const average = Number(data.bottomline?.average_score) || 0;
        summary.append(this.node('span', 'sg-reviews__score', average.toFixed(1)), this.stars(average), this.node('span', '', `${total} ${this.labels.reviews}`));
      }
      summary.hidden = !total;
      this.querySelector('[data-review-toolbar]').hidden = !total;
      if (reset) this.hasGrouped = false;
      this.hasGrouped = this.hasGrouped || Object.keys(data.grouping_data || {}).length > 0 || Object.keys(data.syndication_data || {}).length > 0;
      this.querySelector('[data-review-group-note]').hidden = !this.hasGrouped;
      this.status.textContent = total ? this.labels.loaded.replace('{shown}', this.shown.size).replace('{total}', total) : this.labels.empty;
      this.more.hidden = !reviews.length || page * 10 >= total;
      this.more.textContent = this.labels.more;
    } catch (error) {
      if (!this.isConnected || this.controller !== controller) return;
      this.status.textContent = this.labels.error;
      this.more.hidden = false;
      this.more.textContent = this.labels.retry;
    } finally {
      if (this.controller === controller) {
        this.more.disabled = false;
        this.sort.disabled = false;
        this.list.setAttribute('aria-busy', 'false');
      }
    }
  }
}
if (!customElements.get('sandlot-reviews')) customElements.define('sandlot-reviews', SandlotReviews);
})();
